import React, {useEffect, useMemo, useState} from "react";
import { Button, Checkbox, Icon, Input, Select, Table, TableHeader, TableBody, TableRow, TableHeaderCell, TableCell } from "semantic-ui-react";
import DeleteModal from "../../components/DeleteModal/DeleteModal";
import DimmerLoader from "../../components/DimmerLoader/DimmerLoader";
import TournamentsCRUDForm from "./TournamentsCRUD.form";
import {useAppDispatch, useAppSelector} from "../../hooks/hooks";
import {
    changePageSize,
    clearFilter,
    deleteDartsTournament,
    getAllTourPlayers,
    getDartsTournaments,
    getDartsTournamentsInit,
    setEditTournamentMode,
    setFilter,
    setPageNumber, setTournamentForScheme,
    showTournamentForm
} from "./TournamentsCRUD.slice";
import styles from "./TournamentsCRUD.module.scss"
import { buildQueryString } from "../../helpers/buildQueryString";
import {useDebounce} from "../../hooks/useDebounce";
import AppPagination from "../../components/Pagination/AppPagination";
import CDCTournament from "../../components/CDCTournament/CDCTournament";
import { DartsGameType } from "../../common/types/DartsGameTypes";

const TournamentsCRUD = () => {
    const dispatch = useAppDispatch();
    const {isVisibleForm, isLoading, noContent, dartsTournaments, pagination, seriesOptions, competitionOptions, filter} = useAppSelector(state => state.TournamentsCRUD);
    const cms = useAppSelector(state => state.Auth.cms);
    const [searchWord, setSearchWord] = useState("");
    const [sortField, setSortField] = useState<string>('competitionName');
    const [sortDirection, setSortDirection] = useState<'ascending' | 'descending'>('ascending');
    const [weeks, setWeeks] = useState<{ key: number; value: number; text: string; }[]>([]);

    const [tourType, setTourType] = useState<DartsGameType | null>(null);
    useEffect(() => {
        const params = { pageNum: pagination.activePage, pageSize: pagination.pageSize, sortField, sort: sortDirection === "ascending" ? 0 : 1 }
        dispatch(getDartsTournaments(buildQueryString({...params, ...filter})));
        dispatch(getDartsTournamentsInit())
        dispatch(getAllTourPlayers())
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const onChangePager = (activePage: string | number | undefined) => {
        dispatch(setPageNumber(activePage))
        const params = { pageNum: activePage, pageSize: pagination.pageSize, search: searchWord}
        dispatch(getDartsTournaments(buildQueryString(params)));
    }

    const handleSort = (field: string) => {
        const direction = sortField === field && sortDirection === 'ascending' ? 'descending' : 'ascending';
        setSortField(field);
        setSortDirection(direction);
        const params = {
            pageNum: pagination.activePage,
            pageSize: pagination.pageSize,
            sortField: field,
            sort: direction === "ascending" ? 0 : 1,
            search: searchWord
        }
        dispatch(getDartsTournaments(buildQueryString({...params, ...filter})))
    };

    const onSearch = () => {
        if (searchWord.trim().length < 3) return;
        const params = { pageNum: pagination.activePage, pageSize: pagination.pageSize, sortField, sort: sortDirection === "ascending" ? 0 : 1, search: searchWord}
        dispatch(getDartsTournaments(buildQueryString({...params, ...filter})))
    }

    useDebounce(onSearch, searchWord, 500)

    const phases = useMemo(() => {
        const options = seriesOptions?.map(series => {
            return {key: series.id, value: series.id, text: series.name}
        })
        // options.unshift({key: 0, value: 0, text: "Clear"})
        return options
    }, [seriesOptions]);

    const competitions = useMemo(() => {
        const options = competitionOptions?.map(phase => {
            return {key: phase.id, value: phase.id, text: phase.name}
        })
        // options.unshift({key: 0, value: 0, text: "Clear"})
        return options
    }, [competitionOptions]);

    const setFilterValue = (name: "phase" | "week" | "competition", value: any) => {
        dispatch(setFilter({name, value}))
        const params = {
            pageNum: 1,
            pageSize: pagination.pageSize,
            sortField,
            sort: sortDirection === "ascending" ? 0 : 1,
            search: searchWord
        }
        const filters = {...filter, [name]: value}
        dispatch(getDartsTournaments(buildQueryString({...params, ...filters})))

    }

    const onSelectPhase = (value: any) => {
        const phase = seriesOptions.find(series => series.id === value)
        if (phase) {
            const weekOptions = phase.items?.map(week => {
                return {key: week.id, value: week.id, text: week.name}
            })
            if (weekOptions) {
                weekOptions.unshift({ key: 0, value: 0, text: "Clear" })
                setWeeks(weekOptions)
            }
        }
    }
    const onChangePageSize = (value: any) => {
        dispatch(changePageSize(value))
        const params = {
            pageNum: pagination.activePage,
            pageSize: value,
            sortField,
            sort: sortDirection === "ascending" ? 0 : 1,
            search: searchWord
        }
        dispatch(getDartsTournaments(buildQueryString({...params, ...filter})))
    }

    return (
        <div className="cms-container-general">
            {tourType === DartsGameType.KNOCKOUT && <CDCTournament/>}
            <div className={styles.tableControls}>
                {!!filter.phase &&
                    <Select
                        options={weeks}
                        value={filter.week}
                        onChange={(e, {value}) => {
                            setFilterValue("week", value)
                        }}
                        placeholder='weeks'
                    />}
                <Select
                    options={phases}
                    value={filter.phase}
                    onChange={(e, {value}) => {
                        setFilterValue("phase", value)
                        onSelectPhase(value)
                    }}
                    placeholder='series'
                />
                <Select
                    options={competitions}
                    value={filter.competition}
                    onChange={(e, {value}) => {
                        setFilterValue("competition", value)
                    }}
                    placeholder='competitions'
                />
                <Input
                    onChange={(e) => setSearchWord(e.target.value.trim())}
                    icon='search'
                    placeholder='Search...'
                />
                <Button
                    onClick={() => dispatch(clearFilter())}
                    color="green">
                    Clear filter
                </Button>
                <Button onClick={() => dispatch(showTournamentForm())} color="green">Add</Button>
            </div>
            {noContent ? (
                <div className="schedule-info-block">No tournaments</div>
            ) : (
                    <div className="cms-table-general">
               <Table sortable celled textAlign="center">
                    {isLoading && <DimmerLoader/>}
                    <TableHeader>
                        <TableRow>
                            {cms['id'] &&
                                <TableHeaderCell >Id</TableHeaderCell>
                            }
                                <TableHeaderCell sorted={sortField === 'title' ? sortDirection : undefined}  onClick={() => handleSort('title')}>
                                    Title
                                </TableHeaderCell>
                            <TableHeaderCell  sorted={sortField === 'competitionName' ? sortDirection : undefined}
                                onClick={() => handleSort('competitionName')}>
                                Competition
                            </TableHeaderCell>
                            <TableHeaderCell sorted={sortField === 'week' ? sortDirection : undefined} 
                                onClick={() => handleSort('week')}>
                                Week
                            </TableHeaderCell>
                            <TableHeaderCell sorted={sortField === 'type' ? sortDirection : undefined} 
                                onClick={() => handleSort('type')}>
                                Type
                            </TableHeaderCell>
                            <TableHeaderCell>Is Active</TableHeaderCell>
                            <TableHeaderCell></TableHeaderCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {!noContent && dartsTournaments?.map((tour) => {
                            return (
                                <TableRow key={tour.id}>
                                    {cms['id'] &&
                                        <TableCell >{tour.id}</TableCell>
                                    }
                                    <TableCell>{tour.title}</TableCell>
                                    <TableCell>{tour.competitionName}</TableCell>
                                    <TableCell>{tour.week}</TableCell>
                                    <TableCell>{tour.typeName}</TableCell>
                                    <TableCell>{<Checkbox checked={tour.isActive}/>}</TableCell>
                                    <TableCell>
                                        {cms['delete'] &&
                                            <div className={styles.controlBtn}>
                                                <Icon link name='sitemap' size="small" color="green"
                                                    onClick={() => {
                                                        dispatch(setTournamentForScheme(tour))
                                                        setTourType(tour.type)
                                                    }}
                                                >
                                                </Icon>
                                                <DeleteModal id={tour.id} titleModal={"Delete your Tournament"}
                                                    confirmText={"Are you sure you want to delete your tournament?"}
                                                    deleteAction={(id: number) => dispatch(deleteDartsTournament(id))}
                                                />
                                                <Icon name='pencil alternate' link color="grey" size="small" onClick={() => dispatch(setEditTournamentMode(tour)) } />
                                            </div>
                                        }
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
                    </div>
            )}
            {!!dartsTournaments && (
                <AppPagination props={pagination} onChangePager={onChangePager} onChangePageSize={onChangePageSize} />
            )}
            {isVisibleForm && (
                <TournamentsCRUDForm tournamentsIds={[]} />
            )}
        </div>
    );
};

export default TournamentsCRUD;
